/** Per-display screen-scale calibration.
 *
 *  At "1:1 zoom" the app needs to know how many CSS pixels render to one
 *  physical millimetre on screen. The CSS spec's reference of 96 px/in
 *  (3.7795 px/mm) is only approximate — actual displays vary with their
 *  CSS DPI, OS scaling, and browser zoom. This module owns a per-device
 *  multiplier the user calibrates once with a physical ruler; everything
 *  that needs the project-mm → CSS-px factor reads getPxPerMm() so a new
 *  multiplier takes effect immediately on the next render. */

import {
  CALIBRATION_MAX,
  CALIBRATION_MIN,
  calibrationPref,
} from "./preferences";

const BASE_PX_PER_MM = 96 / 25.4;

/** An uncalibrated display renders at the CSS-spec baseline. The stored
 *  preference decodes to null when absent or out of range (#998), which is
 *  exactly that case. */
function multiplier(): number {
  return calibrationPref.get() ?? 1;
}

export function getPxPerMm(): number {
  return BASE_PX_PER_MM * multiplier();
}

export function getCalibration(): number {
  return multiplier();
}

export function setCalibration(m: number): void {
  if (!isFinite(m)) return;
  const clamped = Math.max(CALIBRATION_MIN, Math.min(CALIBRATION_MAX, m));
  if (clamped === multiplier()) return;
  calibrationPref.set(clamped);
}

export function resetCalibration(): void {
  setCalibration(1);
}

export function onCalibrationChange(fn: (m: number) => void): () => void {
  return calibrationPref.subscribe((v) => fn(v ?? 1));
}

/** Exposed so the calibration dialog can show "BASE_PX_PER_MM × current
 *  multiplier" math to the user if needed. Most callers should use
 *  getPxPerMm() instead. */
export const BASE_PX_PER_MM_VALUE = BASE_PX_PER_MM;
